import { useEffect, useRef, useState } from "react";
import type { ChoiceOption, OpenChoice, TermLanguage } from "../api/types";
import { useCharacter } from "../character";
import { usePendingChoices } from "../pendingChoices";
import { number, source, text } from "../text";
import { LongText } from "./LongText";
import { Stepper } from "./Stepper";

type Selection = string | string[] | Record<string, number> | number;

/**
 * Renders any open choice generically. A complete selection is applied at once; only kinds that cannot tell when the
 * input is finished (free text, numbers, point distributions, "some of") wait for "Übernehmen" or for leaving the page.
 * Embedded, the card has no frame and no heading, because the surrounding list already names the decision.
 * A text choice with options shows them as suggestions: one tap takes a suggestion, the input below takes any other text.
 * Placeholder suggestions stand for a value still to be named and are set in italics.
 */
export function ChoiceCard(props: { choice: OpenChoice; embedded?: boolean }) {
  const { choice } = props;
  const { choose, language } = useCharacter();
  const pending = usePendingChoices();
  const spec = choice.spec;
  const min = spec.min ?? 1;
  const max = spec.max ?? 1;
  const options = spec.options ?? [];
  const values = spec.values ?? [];
  const [single, setSingle] = useState<string>("");
  const [many, setMany] = useState<string[]>([]);
  const [assignment, setAssignment] = useState<Record<string, number>>({});
  const [distribution, setDistribution] = useState<Record<string, number>>({});
  const initial = spec.initial ?? null;
  const [free, setFree] = useState(initial ?? "");
  useEffect(() => setFree(initial ?? ""), [initial]);

  const groups = [...new Set(options.map((o) => groupName(o, language)))];
  const grouped = groups.length > 1 && (spec.kind === "pickOne" || spec.kind === "pickMany");
  const [group, setGroup] = useState<string | null>(null);
  const visible = grouped ? options.filter((o) => groupName(o, language) === group) : options;

  const used = Object.values(distribution).reduce((a, b) => a + b, 0);
  const assign = assignFromMap(values, assignment);
  let selection: Selection | null = null;
  let valid = false;
  switch (spec.kind) {
    case "pickOne":
      selection = single;
      valid = single !== "";
      break;
    case "pickMany":
      selection = many;
      valid = many.length >= min && many.length <= max;
      break;
    case "assignValues":
      selection = assign;
      valid = assign !== null;
      break;
    case "distribute":
      selection = distribution;
      valid = spec.budget == null || used === spec.budget;
      break;
    case "number":
      selection = Number(free);
      valid = free !== "" && !Number.isNaN(Number(free));
      break;
    default:
      selection = free;
      // With a current value the input may also be cleared, which removes the value.
      valid = initial == null ? free.trim() !== "" : free.trim() !== initial.trim();
  }
  const manual = spec.kind === "distribute" || spec.kind === "number" || spec.kind === "text" || (spec.kind === "pickMany" && min < max);

  const resolve = (value: Selection) =>
    choose(
      { type: choice.resolveOperation, payload: { ...(choice.resolvePayload as Record<string, unknown>), [choice.resolveSelectionField ?? "selection"]: value } },
      text(choice.label, language),
    );

  const latest = useRef({ valid, selection, manual, resolve });
  latest.current = { valid, selection, manual, resolve };
  useEffect(() => {
    if (!pending) return;
    pending.register(choice.id, () => {
      const { valid, selection, manual, resolve } = latest.current;
      return manual && valid && selection != null ? resolve(selection) : Promise.resolve(true);
    });
    return () => pending.register(choice.id, null);
  }, [pending, choice.id]);

  const submit = () => {
    if (valid && selection != null) void resolve(selection);
  };
  const groupLabel = text(spec.groupLabel, language) || "Gruppe";
  const distinctValues = [...new Set(values)];
  const countOf = (value: number) => values.filter((v) => v === value).length;
  const assignedCount = (value: number) => Object.values(assignment).filter((v) => v === value).length;

  return (
    <section className={props.embedded ? "choice embedded" : "choice card"} data-choice={choice.id}>
      {!props.embedded && (
        <header>
          <h3>
            {text(choice.label, language)}
            {choice.required && <span className="badge required">Pflicht</span>}
          </h3>
          {choice.source && <span className="source">{source(choice.source)}</span>}
        </header>
      )}
      {choice.description && <p className="muted small">{text(choice.description, language)}</p>}

      {grouped && (
        <div className="choice-groups" role="group" aria-label={groupLabel}>
          <p className="muted small">{groupLabel} wählen:</p>
          {groups.map((g) => (
            <button key={g} type="button" className={`chip${g === group ? " selected" : ""}`} aria-pressed={g === group} onClick={() => setGroup(g)}>
              {g || "Weitere"}
            </button>
          ))}
        </div>
      )}

      {spec.kind === "pickOne" && (!grouped || group !== null) && (
        <div className="option-list" role="radiogroup">
          {visible.map((o) => (
            <div key={o.value} className="option-row">
              <label className={`option${o.disabled ? " disabled" : ""}`}>
                <input
                  type="radio"
                  name={choice.id}
                  value={o.value}
                  disabled={o.disabled}
                  checked={single === o.value}
                  onChange={() => {
                    setSingle(o.value);
                    void resolve(o.value);
                  }}
                />
                <span>
                  <strong>{text(o.label, language)}</strong>
                  {o.description && <span className="muted"> {text(o.description, language)}</span>}
                  {o.disabled && o.disabledReason && <span className="muted"> ({text(o.disabledReason, language)})</span>}
                </span>
                {o.source && <span className="source">{source(o.source)}</span>}
              </label>
              <LongText value={o.longText} language={language} />
            </div>
          ))}
        </div>
      )}

      {spec.kind === "pickMany" && (
        <div className="option-list">
          <p className="muted small">
            {min === max ? `Genau ${min} wählen` : `${min} bis ${max} wählen`} ({many.length} gewählt)
          </p>
          {(!grouped || group !== null) &&
            visible.map((o) => (
              <div key={o.value} className="option-row">
                <label className={`option${o.disabled ? " disabled" : ""}`}>
                  <input
                    type="checkbox"
                    disabled={o.disabled || (!many.includes(o.value) && many.length >= max)}
                    checked={many.includes(o.value)}
                    onChange={(e) => {
                      const next = e.target.checked ? [...many, o.value] : many.filter((v) => v !== o.value);
                      setMany(next);
                      if (!manual && next.length === max) void resolve(next);
                    }}
                  />
                  <span>
                    {text(o.label, language)}
                    {o.description && <span className="muted"> {text(o.description, language)}</span>}
                  </span>
                  {o.source && <span className="source">{source(o.source)}</span>}
                </label>
                <LongText value={o.longText} language={language} />
              </div>
            ))}
        </div>
      )}

      {spec.kind === "assignValues" && (
        <div className="assign-list">
          <p className="muted small">
            Werte {values.map((v) => number(v)).join(", ")} auf {options.length} Möglichkeiten verteilen; jede Möglichkeit höchstens einmal.
          </p>
          {options.map((o) => {
            const current = assignment[o.value];
            return (
              <div key={o.value} className={`assign-row${o.disabled ? " disabled" : ""}`}>
                <span className="assign-label">
                  {text(o.label, language)}
                  {o.description && <span className="muted small"> {text(o.description, language)}</span>}
                </span>
                <span className="assign-values" role="group" aria-label={text(o.label, language)}>
                  {distinctValues.map((v) => {
                    const held = current === v;
                    const exhausted = assignedCount(v) >= countOf(v) && !held;
                    return (
                      <button
                        key={v}
                        type="button"
                        className={`chip${held ? " selected" : ""}`}
                        aria-pressed={held}
                        disabled={o.disabled || exhausted}
                        onClick={() => {
                          const next = { ...assignment };
                          if (held) delete next[o.value];
                          else next[o.value] = v;
                          setAssignment(next);
                          const complete = assignFromMap(values, next);
                          if (complete) void resolve(complete);
                        }}
                      >
                        {v > 0 ? `+${number(v)}` : number(v)}
                      </button>
                    );
                  })}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {spec.kind === "distribute" && (
        <div className="distribute-list">
          <p className="muted small">
            {spec.budget != null ? `${number(used)} von ${number(spec.budget)} Punkten verteilt` : `${number(used)} Punkte verteilt`}
            {spec.perTargetMax != null && `, höchstens ${number(spec.perTargetMax)} je Ziel`}
          </p>
          {options.map((o) => {
            const current = distribution[o.value] ?? 0;
            const left = spec.budget != null ? spec.budget - used : Number.POSITIVE_INFINITY;
            const cap = Math.min(spec.perTargetMax ?? Number.POSITIVE_INFINITY, current + Math.max(0, left));
            return (
              <div key={o.value} className="assign-row">
                <span className="assign-label">{text(o.label, language)}</span>
                <Stepper label={text(o.label, language)} value={current} min={0} max={cap} onChange={(v) => setDistribution({ ...distribution, [o.value]: v })} />
              </div>
            );
          })}
        </div>
      )}

      {spec.kind === "text" && options.length > 0 && (
        <div role="group" aria-label="Vorschläge">
          <p className="muted small">Vorschläge:</p>
          <div className="chips">
            {options.map((o) => (
              <button
                key={o.value}
                type="button"
                className={o.placeholder ? "chip placeholder" : "chip"}
                disabled={o.disabled}
                title={
                  o.disabled && o.disabledReason
                    ? text(o.disabledReason, language)
                    : o.placeholder
                      ? "Platzhalter: danach wird das konkrete Thema erfragt"
                      : undefined
                }
                onClick={() => void resolve(o.value)}
              >
                {text(o.label, language)}
                {o.disabled && o.disabledReason && <span className="muted"> ({text(o.disabledReason, language)})</span>}
              </button>
            ))}
          </div>
          {options.some((o) => o.placeholder) && (
            <p className="muted small">Kursive Vorschläge sind Platzhalter: Statt ihrer das konkrete Thema eintragen.</p>
          )}
        </div>
      )}

      {(spec.kind === "text" || spec.kind === "number") && (
        <label className="field">
          {spec.kind === "text" && options.length > 0 && <span className="muted small">Eigene Angabe</span>}
          <input
            type={spec.kind === "number" ? "number" : "text"}
            value={free}
            onChange={(e) => setFree(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submit()}
          />
        </label>
      )}

      {manual && (
        <div className="actions">
          <button type="button" className="button primary" disabled={!valid} onClick={submit}>
            Übernehmen
          </button>
        </div>
      )}
    </section>
  );
}

/**
 * The option per value, in the order of the values, or null while values are still unassigned.
 * Equal values are interchangeable, so the first unused option with that value takes the slot.
 */
function assignFromMap(values: number[], assignment: Record<string, number>): string[] | null {
  const remaining = Object.entries(assignment);
  const result: string[] = [];
  for (const value of values) {
    const index = remaining.findIndex(([, v]) => v === value);
    if (index < 0) return null;
    result.push(remaining[index]![0]);
    remaining.splice(index, 1);
  }
  return result;
}

function groupName(option: ChoiceOption, language: TermLanguage): string {
  return text(option.group, language);
}
